import React from "react";
import { formatIDR } from "../../../utils/helpers/formatIDR";
import ModalShell from "../../CashBankPage/components/ModalShell";
import { STATUS_META, formatDate, formatDateTime } from "../constants";

const DetailModal = ({ asset, onClose }) => {
  const status = STATUS_META[asset?.status] || { label: asset?.status || "-", className: "bg-secondary-lt" };

  return (
    <ModalShell
      eyebrow="DETAIL ASET"
      title={asset?.asset_no || "Aset & Penyusutan"}
      subtitle="Nilai buku, jadwal penyusutan, dan jejak kas dalam satu tampilan."
      onClose={onClose}
      size="xl">
      <div className="fixed-asset-detail-hero">
        <div>
          <span>{asset?.category_name || "Kategori aset"}</span>
          <strong>{asset?.name || "-"}</strong>
          <small>{asset?.location || "Lokasi belum dicatat"}</small>
        </div>
        <div className="text-end">
          <span>Nilai buku saat ini</span>
          <strong>{formatIDR(asset?.book_value)}</strong>
          <span className={`badge ${status.className}`}>{status.label}</span>
        </div>
      </div>

      <div className="fixed-asset-detail-grid">
        <div><span>Harga perolehan</span><strong>{formatIDR(asset?.acquisition_cost)}</strong><small>{formatDate(asset?.acquired_at)}</small></div>
        <div><span>Akumulasi penyusutan</span><strong>{formatIDR(asset?.accumulated_depreciation)}</strong><small>Sampai hari ini</small></div>
        <div><span>Penyusutan bulanan</span><strong>{formatIDR(asset?.monthly_depreciation)}</strong><small>Metode garis lurus</small></div>
        <div><span>Nilai residu</span><strong>{formatIDR(asset?.residual_value)}</strong><small>Umur manfaat {asset?.useful_life_months || 0} bulan</small></div>
      </div>

      <section className="fixed-asset-detail-section">
        <div className="fixed-asset-section-heading"><div><h4>Informasi aset</h4><p>Data referensi untuk inventaris dan pemeriksaan fisik.</p></div></div>
        <div className="fixed-asset-metadata-grid">
          <div><span>Akun pembayaran</span><strong>{asset?.account_name || "-"}</strong><small>Saldo kini {formatIDR(asset?.account_current_balance)}</small></div>
          <div><span>Supplier / vendor</span><strong>{asset?.supplier_name || "-"}</strong><small>Sumber perolehan aset</small></div>
          <div><span>Mulai digunakan</span><strong>{formatDate(asset?.available_for_use_at)}</strong><small>Awal perhitungan penyusutan</small></div>
          <div><span>Nomor seri</span><strong>{asset?.serial_number || "-"}</strong><small>Identitas perangkat</small></div>
        </div>
        <div className="fixed-asset-note">{asset?.note || "Tidak ada keterangan tambahan."}</div>
      </section>

      {asset?.status === "cancelled" ? (
        <div className="fixed-asset-cancellation">
          <div><strong>Aset dibatalkan</strong><span>{formatDateTime(asset?.cancelled_at)} · {asset?.cancelled_by_name || "Sistem"}</span></div>
          <p>{asset?.cancellation_reason || "Tidak ada alasan pembatalan."}</p>
        </div>
      ) : null}

      <section className="fixed-asset-detail-section">
        <div className="fixed-asset-section-heading">
          <div><h4>Jadwal penyusutan</h4><p>Baris yang jatuh tempo otomatis masuk ke biaya dan laba bersih laporan.</p></div>
          <span className="fixed-asset-record-count">{asset?.depreciations?.length || 0} periode</span>
        </div>
        <div className="table-responsive fixed-asset-schedule-table">
          <table className="table table-vcenter card-table mb-0">
            <thead><tr><th>Ke-</th><th>Periode</th><th>Tanggal pengakuan</th><th className="text-end">Penyusutan</th><th>Status</th></tr></thead>
            <tbody>
              {(asset?.depreciations || []).map((row) => {
                const cancelled = row.status === "cancelled";
                const recognized = Boolean(row.is_recognized);
                return (
                  <tr key={row.id}>
                    <td className="text-secondary">{row.sequence}</td>
                    <td>{formatDate(row.period_start)} – {formatDate(row.period_end)}</td>
                    <td>{formatDate(row.depreciation_date)}</td>
                    <td className="text-end fw-bold">{formatIDR(row.amount)}</td>
                    <td><span className={`badge ${cancelled ? "bg-danger-lt" : recognized ? "bg-success-lt" : "bg-secondary-lt"}`}>{cancelled ? "Dibatalkan" : recognized ? "Diakui" : "Terjadwal"}</span></td>
                  </tr>
                );
              })}
              {!asset?.depreciations?.length ? <tr><td colSpan="5" className="text-center text-secondary py-4">Jadwal penyusutan belum tersedia.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="fixed-asset-detail-section">
        <div className="fixed-asset-section-heading"><div><h4>Jejak audit kas</h4><p>Perolehan dan pembatalan disimpan sebagai mutasi yang tidak dihapus.</p></div></div>
        <div className="table-responsive fixed-asset-audit-table">
          <table className="table table-vcenter card-table mb-0">
            <thead><tr><th>Waktu</th><th>Aktivitas</th><th>Nominal</th><th>Saldo sebelum</th><th>Saldo sesudah</th><th>Pengguna</th></tr></thead>
            <tbody>
              {(asset?.events || []).map((event) => {
                const acquired = event.event_type === "acquired";
                return (
                  <tr key={event.id}>
                    <td>{formatDateTime(event.occurred_at)}</td>
                    <td><span className={`badge ${acquired ? "bg-secondary-lt" : "bg-danger-lt"}`}>{acquired ? "Perolehan" : "Reversal"}</span>{event.reason ? <small className="d-block text-secondary mt-1">{event.reason}</small> : null}</td>
                    <td className={`fw-bold ${acquired ? "text-danger" : "text-primary"}`}>{acquired ? "−" : "+"}{formatIDR(Math.abs(Number(event.signed_amount || 0)))}</td>
                    <td>{formatIDR(event.balance_before)}</td>
                    <td className="fw-bold">{formatIDR(event.balance_after)}</td>
                    <td>{event.created_by_name || "Sistem"}</td>
                  </tr>
                );
              })}
              {!asset?.events?.length ? <tr><td colSpan="6" className="text-center text-secondary py-4">Jejak audit belum tersedia.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>
    </ModalShell>
  );
};

export default DetailModal;
